import * as ed from '@noble/ed25519'

/**
 * TS strict-режим требует, чтобы BufferSource был именно ArrayBuffer, а не ArrayBufferLike
 * (который включает SharedArrayBuffer). Uint8Array.buffer — ArrayBufferLike, поэтому
 * копируем в гарантированный ArrayBuffer перед вызовом crypto.subtle.
 */
function toArrayBuffer(view: Uint8Array): ArrayBuffer {
  const buf = new ArrayBuffer(view.byteLength)
  new Uint8Array(buf).set(view)
  return buf
}

// @noble/ed25519 v2 использует crypto.subtle.digest('SHA-512', …) через sha512Async.
// SHA-512 в SubtleCrypto поддерживается везде, начиная с Chromium 37 — включая
// самые старые сборки OBS Browser Source (CEF), поэтому дополнительный полифилл
// синхронного хеша не нужен: мы всегда вызываем verifyAsync.

/**
 * Верификация ПОДПИСИ кода плагина асимметричным алгоритмом Ed25519.
 *
 * ── Почему Ed25519, а не HMAC ──────────────────────────────────────────────────
 *   Раньше использовался HMAC-SHA256 с общим секретом (VITE_PLUGIN_HMAC_SECRET).
 *   Проблема: любой VITE_*-секрет инлайнится в публичный бандл, поэтому секрет
 *   утекал в браузер и позволял подписывать произвольный код → RCE-вектор.
 *
 *   Теперь подпись асимметричная:
 *     • Бэкенд держит ПРИВАТНЫЙ ключ и подписывает им payload плагина.
 *     • Фронт получает только ПУБЛИЧНЫЙ ключ (после авторизации) и лишь
 *       проверяет подпись. Утечка публичного ключа безопасна.
 *
 * ── Транспорт верификации: native + fallback ───────────────────────────────────
 *   Поддержка `crypto.subtle.verify('Ed25519', …)` появилась в Chromium 137
 *   (март 2025). OBS Browser Source отстаёт: OBS 30.x = Chromium 103, OBS 31.x =
 *   Chromium 127. Там нативный Ed25519 бросает NotSupportedError, из-за чего
 *   ВСЕ плагины отвергаются и overlay остаётся пустым, хотя WS «live».
 *
 *   Стратегия:
 *     1) Пробуем нативный WebCrypto Ed25519.
 *     2) Если importKey ИЛИ verify падает с NotSupportedError/DOMException —
 *        помечаем `nativeSupported = false` и переключаемся на @noble/ed25519
 *        (чистый JS, работает во всех CEF, ~15 KB gzipped).
 *
 * ── Формат данных ──────────────────────────────────────────────────────────────
 *   Публичный ключ: raw Ed25519 (32 байта), закодированный в base64url,
 *                   ИЛИ SPKI (DER), закодированный в base64/base64url.
 *   Подпись:        raw Ed25519 (64 байта), закодированная в base64/base64url.
 *   Payload:        КАНОНИЧЕСКИЙ JSON { pluginType, version, moduleCodeHash, keyId }
 *                   с фиксированным порядком ключей (идентично бэкенду).
 *
 * ── Безопасность ───────────────────────────────────────────────────────────────
 *   Fail-closed: если публичный ключ/keyId не загружены — verify() возвращает false.
 *   DEV-bypass отсутствует: без ключа плагин не загружается ни в одном режиме.
 *   Дополнительно сверяется sha256 содержимого модуля и keyId.
 */
export interface VerifyParams {
  pluginType: string
  version: string
  moduleCode: string
  moduleCodeHash: string
  signature: string
  keyId: string
}

export type VerifyFailureReason =
  | { code: 'no-key' }
  | { code: 'key-id-mismatch'; expected: string; actual: string }
  | { code: 'hash-mismatch'; expected: string; actual: string }
  | { code: 'import-error' }
  | { code: 'bad-signature'; detail?: string }
  | { code: 'verify-throw'; detail: string }

export type VerifyTransport = 'native' | 'noble' | 'pending'

export class PluginVerifier {
  private rawKeyB64: string | null = null
  private keyId: string | null = null

  // Native (WebCrypto Ed25519) ветка.
  private nativeKeyPromise: Promise<CryptoKey | null> | null = null

  // Noble fallback: сырой публичный ключ (32 байта).
  private nobleKeyBytes: Uint8Array | null = null

  /**
   * null   — ещё не пробовали (первый verify выяснит),
   * true   — WebCrypto Ed25519 работает,
   * false  — WebCrypto Ed25519 не поддерживается, используем @noble/ed25519.
   */
  private nativeSupported: boolean | null = null

  /**
   * Устанавливает (или заменяет) публичный ключ и его keyId. Вызывается после
   * авторизации, когда фронт получил ключ с бэкенда. Сбрасывает импортированные
   * ключи, но НЕ сбрасывает флаг `nativeSupported`: capability платформы одна
   * и та же для сессии.
   */
  setPublicKey(publicKeyB64: string | null, keyId: string | null): void {
    this.rawKeyB64 = publicKeyB64 && publicKeyB64.trim() ? publicKeyB64.trim() : null
    this.keyId = keyId && keyId.trim() ? keyId.trim() : null
    this.nativeKeyPromise = null
    this.nobleKeyBytes = null
  }

  /** Есть ли загруженный публичный ключ. */
  hasKey(): boolean {
    return this.rawKeyB64 !== null && this.keyId !== null
  }

  /** Какой транспорт используется сейчас (для диагностики в консоли). */
  getTransport(): VerifyTransport {
    if (this.nativeSupported === true) return 'native'
    if (this.nativeSupported === false) return 'noble'
    return 'pending'
  }

  /**
   * Причина, по которой verify провалился. Полезна для отладки:
   *   • no-key            — публичный ключ не загружен вообще
   *   • key-id-mismatch   — backend поменял ключ (частый случай при рестарте)
   *   • hash-mismatch     — moduleCode повреждён или не совпадает с moduleCodeHash
   *   • bad-signature     — Ed25519 подпись невалидна для payload+ключа
   *   • import-error      — не удалось импортировать публичный ключ
   *   • verify-throw      — исключение внутри crypto.subtle.verify / noble.verify
   */
  private lastFailure: VerifyFailureReason | null = null

  getLastFailure(): VerifyFailureReason | null {
    return this.lastFailure
  }

  async verify(params: VerifyParams): Promise<boolean> {
    this.lastFailure = null

    // (1) Fail-closed: без ключа/keyId не проверяем.
    if (!this.rawKeyB64 || !this.keyId) {
      this.lastFailure = { code: 'no-key' }
      return false
    }

    // (2) keyId сообщения должен совпасть с загруженным.
    if (params.keyId !== this.keyId) {
      this.lastFailure = {
        code: 'key-id-mismatch',
        expected: this.keyId,
        actual: params.keyId,
      }
      return false
    }

    // (3) Пересчитываем sha256 кода и сверяем с заявленным хешем.
    const actualHash = await this.sha256Hex(params.moduleCode)
    if (actualHash !== params.moduleCodeHash) {
      this.lastFailure = {
        code: 'hash-mismatch',
        expected: params.moduleCodeHash,
        actual: actualHash,
      }
      return false
    }

    const sigBytes = this.b64ToBytes(params.signature)
    if (!sigBytes) {
      this.lastFailure = { code: 'bad-signature', detail: 'signature is not valid base64' }
      return false
    }

    // (4) Канонический payload — тот же порядок ключей, что на бэкенде.
    const payload = new TextEncoder().encode(
      JSON.stringify({
        pluginType: params.pluginType,
        version: params.version,
        moduleCodeHash: params.moduleCodeHash,
        keyId: params.keyId,
      }),
    )

    // (5) Выбираем транспорт.
    if (this.nativeSupported === null || this.nativeSupported === true) {
      const nativeResult = await this.verifyNative(sigBytes, payload)
      if (nativeResult !== 'unsupported') return nativeResult
      // Первый unsupported → фиксируем и падаем в noble.
      this.nativeSupported = false
    }

    return this.verifyNoble(sigBytes, payload)
  }

  /**
   * Пытается верифицировать через нативный WebCrypto Ed25519.
   * Возвращает 'unsupported', если платформа не умеет — тогда переключаемся на noble.
   */
  private async verifyNative(
    sig: Uint8Array,
    payload: Uint8Array,
  ): Promise<boolean | 'unsupported'> {
    let key: CryptoKey | null
    try {
      key = await this.getNativeKey()
    } catch (err) {
      if (this.isUnsupportedError(err)) return 'unsupported'
      this.lastFailure = { code: 'import-error' }
      return false
    }
    if (!key) {
      this.lastFailure = { code: 'import-error' }
      return false
    }

    try {
      const ok = await crypto.subtle.verify(
        'Ed25519',
        key,
        toArrayBuffer(sig),
        toArrayBuffer(payload),
      )
      if (!ok) this.lastFailure = { code: 'bad-signature' }
      this.nativeSupported = true
      return ok
    } catch (err) {
      if (this.isUnsupportedError(err)) return 'unsupported'
      this.lastFailure = { code: 'verify-throw', detail: String(err) }
      return false
    }
  }

  /** Fallback: @noble/ed25519 — чистый JS, работает в старых CEF/OBS. */
  private async verifyNoble(sig: Uint8Array, payload: Uint8Array): Promise<boolean> {
    let pk = this.nobleKeyBytes
    if (!pk) {
      pk = this.buildNoblePublicKey()
      if (!pk) {
        this.lastFailure = { code: 'import-error' }
        return false
      }
      this.nobleKeyBytes = pk
    }

    try {
      const ok = await ed.verifyAsync(sig, payload, pk)
      if (!ok) this.lastFailure = { code: 'bad-signature' }
      return ok
    } catch (err) {
      this.lastFailure = { code: 'verify-throw', detail: String(err) }
      return false
    }
  }

  /**
   * OBS/CEF без Ed25519 обычно бросают NotSupportedError или DOMException 'NotSupportedError'.
   * Некоторые сборки шлют OperationError; трактуем это тоже как unsupported, если native ещё не был подтверждён.
   */
  private isUnsupportedError(err: unknown): boolean {
    if (this.nativeSupported === true) return false // уже подтвердили — реальная ошибка
    if (err instanceof Error) {
      const name = err.name
      const msg = err.message.toLowerCase()
      if (name === 'NotSupportedError') return true
      if (msg.includes('unsupported') || msg.includes('unrecognized name')) return true
      if (msg.includes('ed25519') && msg.includes('not')) return true
    }
    return false
  }

  /** sha256 строки → hex (lowercase). SubtleCrypto.digest есть везде, включая старый CEF. */
  private async sha256Hex(input: string): Promise<string> {
    const bytes = new TextEncoder().encode(input)
    const digest = await crypto.subtle.digest('SHA-256', bytes)
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
  }

  // ── Native импорт ключа ──────────────────────────────────────────────────────

  private getNativeKey(): Promise<CryptoKey | null> {
    if (!this.nativeKeyPromise) {
      this.nativeKeyPromise = this.importNativeKey()
    }
    return this.nativeKeyPromise
  }

  private async importNativeKey(): Promise<CryptoKey | null> {
    const b64 = this.rawKeyB64
    if (!b64) return null

    const bytes = this.b64ToBytes(b64)
    if (!bytes) return null

    // raw Ed25519 = ровно 32 байта. Иначе считаем что это SPKI (DER).
    const format: 'raw' | 'spki' = bytes.byteLength === 32 ? 'raw' : 'spki'

    return crypto.subtle.importKey(
      format,
      toArrayBuffer(bytes),
      { name: 'Ed25519' },
      false,
      ['verify'],
    )
  }

  // ── Noble ключ ───────────────────────────────────────────────────────────────

  /**
   * @noble/ed25519 принимает ТОЛЬКО raw 32-байтовый публичный ключ.
   * Если пришёл SPKI (44 байта) — вытаскиваем последние 32 байта (сам key material).
   */
  private buildNoblePublicKey(): Uint8Array | null {
    const b64 = this.rawKeyB64
    if (!b64) return null
    const bytes = this.b64ToBytes(b64)
    if (!bytes) return null
    if (bytes.length === 32) return bytes
    if (bytes.length >= 32) return bytes.slice(bytes.length - 32)
    return null
  }

  // ── Утилиты кодирования ─────────────────────────────────────────────────────

  /** Декодирует base64 ИЛИ base64url в Uint8Array. */
  private b64ToBytes(input: string): Uint8Array | null {
    try {
      // Нормализуем base64url → base64
      let b64 = input.replace(/-/g, '+').replace(/_/g, '/')
      const pad = b64.length % 4
      if (pad === 2) b64 += '=='
      else if (pad === 3) b64 += '='
      else if (pad === 1) return null

      const binary = atob(b64)
      const out = new Uint8Array(binary.length)
      for (let i = 0; i < binary.length; i++) {
        out[i] = binary.charCodeAt(i)
      }
      return out
    } catch {
      return null
    }
  }
}

export const pluginVerifier = new PluginVerifier()
